import { type ReactNode } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Icon } from '@socialchorus/shared-ui-components';
import { useProgram } from '../../../../common/use-program';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import { programSelectors } from '../../../../models/program';
import {
  MAX_IMAGES,
  MAX_IMAGE_SIZE_MB,
  ACCEPTED_IMAGE_TYPES,
} from './use-image-uploader';
import { MAX_VIDEOS, ACCEPTED_VIDEO_TYPES } from './use-video-uploader';
import {
  MAX_FILES,
  MAX_FILE_SIZE_MB,
  ACCEPTED_FILE_TYPES,
} from './use-file-uploader';
import './dropzone.scss';

const DEFAULT_MAX_VIDEO_SIZE_MB = 300;

type DropzoneProps = {
  maxSize?: number;
  maxFiles?: number;
  onDropAccepted: (files: File[]) => void;
  onDropRejected: (rejections: FileRejection[]) => void;
  children: ReactNode;
};

const Dropzone = ({
  maxSize,
  maxFiles,
  onDropAccepted,
  onDropRejected,
  children,
}: DropzoneProps) => {
  const { t } = useTranslation();
  const program = useProgram();
  const maxVideoSizeMB = Math.round(
    (Number(program.max_video_upload_size_in_bytes) ||
      DEFAULT_MAX_VIDEO_SIZE_MB * 1024 * 1024) /
      (1024 * 1024)
  );

  // Match the "Add to Post" toolbar (post-media.tsx): a disabled type can't be
  // added via drag-and-drop either, so the browser's drop filter and the drag
  // overlay's hint text shouldn't advertise it in the first place.
  const imagesAttachmentEnabled = usePatronSelector(
    programSelectors.getUserGeneratedContentImagesEnabled
  );
  const videosAttachmentEnabled = usePatronSelector(
    programSelectors.getUserGeneratedContentVideosEnabled
  );
  const filesAttachmentEnabled = usePatronSelector(
    programSelectors.getUserGeneratedContentFilesEnabled
  );
  const accept = [
    imagesAttachmentEnabled && ACCEPTED_IMAGE_TYPES,
    videosAttachmentEnabled && ACCEPTED_VIDEO_TYPES,
    filesAttachmentEnabled && ACCEPTED_FILE_TYPES,
  ]
    .filter(Boolean)
    .join(', ');

  // An empty `accept` string means "no restriction" to react-dropzone, not
  // "reject everything" — so when every file-based type is disabled, disable
  // the dropzone outright rather than relying on an unset accept filter.
  const noDroppableTypesEnabled =
    !imagesAttachmentEnabled &&
    !videosAttachmentEnabled &&
    !filesAttachmentEnabled;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxSize,
    maxFiles,
    multiple: true,
    noClick: true,
    disabled: noDroppableTypesEnabled,
    onDropAccepted,
    onDropRejected,
  });

  return (
    <div
      {...getRootProps()}
      role={undefined}
      tabIndex={undefined}
      className="post-form__content"
    >
      <input {...getInputProps()} />
      {isDragActive && (
        <div className="post-media-drag-overlay">
          <Icon size={42}>add</Icon>
          <span className="text-subheading">
            {t('user_generated_content.media.drag.drop_files')}
          </span>
          {imagesAttachmentEnabled && (
            <span className="text-caption">
              {t('user_generated_content.media.drag.drop_files_hint_images', {
                max: MAX_IMAGES,
                maxSizeMB: MAX_IMAGE_SIZE_MB,
              })}
            </span>
          )}
          {videosAttachmentEnabled && (
            <span className="text-caption">
              {t('user_generated_content.media.drag.drop_files_hint_videos', {
                max: MAX_VIDEOS,
                maxSizeMB: maxVideoSizeMB,
              })}
            </span>
          )}
          {filesAttachmentEnabled && (
            <span className="text-caption">
              {t('user_generated_content.media.drag.drop_files_hint_files', {
                max: MAX_FILES,
                maxSizeMB: MAX_FILE_SIZE_MB,
              })}
            </span>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

export default Dropzone;
